import { useEffect, useRef } from 'react';
import { comboOfEvent, normalizeKeyCombo } from '@sp/core';
import { api } from './api';
import type { ShortcutDefinition } from './types';

/**
 * Keyboard shortcuts.
 *
 * The server owns the mapping (`keyboard_shortcut`, seeded and editable by an admin), so
 * the till follows whatever the store has configured instead of hard-coding F-keys in the
 * UI. This module loads that table and exposes a `keydown` subscription that ignores the
 * keystroke while the cashier is typing in a field.
 *
 * Actions:
 *   productSearch   focus the catalogue search box
 *   customer        attach or change the customer on this bill
 *   customerLookup  search the customer by mobile number
 *   quantity        jump to the quantity box of the selected line
 *   removeItem      remove the selected line
 *   holdBill        park the bill and start a new one
 *   discount        apply a bill discount
 *   resumeBill      bring back a held bill
 *   payment         take payment
 *   reprintReceipt  reprint the receipt just printed
 *   newSale         clear the screen for the next customer
 */

const FALLBACK: ShortcutDefinition[] = [
  { action: 'productSearch', keyCode: 'F2', label: 'Product search', isEnabled: true },
  { action: 'customer', keyCode: 'F3', label: 'Attach customer', isEnabled: true },
  { action: 'quantity', keyCode: 'F4', label: 'Edit quantity', isEnabled: true },
  { action: 'removeItem', keyCode: 'F5', label: 'Remove line', isEnabled: true },
  { action: 'holdBill', keyCode: 'F6', label: 'Hold bill', isEnabled: true },
  { action: 'discount', keyCode: 'F7', label: 'Apply discount', isEnabled: true },
  { action: 'customerLookup', keyCode: 'F8', label: 'Customer lookup', isEnabled: true },
  { action: 'resumeBill', keyCode: 'F9', label: 'Resume held bill', isEnabled: true },
  { action: 'payment', keyCode: 'F10', label: 'Take payment', isEnabled: true },
  { action: 'reprintReceipt', keyCode: 'F11', label: 'Reprint last receipt', isEnabled: true },
  // F12 opens DevTools and cannot be preventDefaulted: a till bound to it always fires
  // the browser alongside the action. New sale lives on Alt+N instead.
  { action: 'newSale', keyCode: 'Alt+N', label: 'New sale', isEnabled: true },
];

let cache: ShortcutDefinition[] = [];
let loaded = false;

function table(): ShortcutDefinition[] {
  return cache.length > 0 ? cache : FALLBACK;
}

export async function loadShortcuts(): Promise<ShortcutDefinition[]> {
  if (loaded) return table();
  try {
    const result = await api.get<{ shortcuts: ShortcutDefinition[] }>('/api/settings/shortcuts');
    cache = result.shortcuts.filter((entry) => entry.isEnabled);
    loaded = true;
  } catch {
    // A till that cannot read its shortcut table must still work, so fall back to the
    // documented defaults rather than disabling the keys the cashier already knows.
    cache = FALLBACK;
  }
  return table();
}

/** The active shortcut table, for the legend and the help overlay. */
export function listShortcuts(): ShortcutDefinition[] {
  return table().filter((entry) => entry.isEnabled);
}

export function keyForAction(action: string): string | undefined {
  return table().find((entry) => entry.action === action)?.keyCode;
}

export function shortcutLabel(action: string): string | undefined {
  return table().find((entry) => entry.action === action)?.label ?? undefined;
}

/**
 * True when a field owns the keystroke rather than the till. Only Escape is decided here: every other
 * key the till listens for is a function key or carries Ctrl/Alt, and neither is text anyone typed.
 * A field marked `data-shortcuts="allow"` (the scan box, which holds focus nearly all day) opts out
 * of even that, because it is the till's own input rather than a field being typed into.
 */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.dataset.shortcuts === 'allow') return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * The key combination a keystroke stands for, if it may be a shortcut at all.
 *
 *  - Never while an input method is composing: typing Tamil goes through an IME, and its keys must
 *    reach the field, not the till.
 *  - Never a plain letter, digit or Enter: that is what a barcode scanner types.
 *  - Never a held-down repeat, so a toggle or a delete fires once per press.
 */
export function shortcutCombo(event: KeyboardEvent): string | null {
  if (event.repeat || event.isComposing || event.keyCode === 229) return null;
  const combo = comboOfEvent(event);
  if (!combo) return null;
  const functionKey = /^F([1-9]|1[0-2])$/.test(combo) || combo === 'Escape';
  const modified = event.ctrlKey || event.altKey || event.metaKey;
  // Only a plain character can be typing, and those are what a barcode scanner sends. A function
  // key or a Ctrl/Alt combination is never text the cashier typed into the field, so it reaches the
  // till from any field: F10 must work while the cursor sits in a quantity box.
  if (!functionKey && !modified) return null;
  // Escape is the one exception: a field (or a picker it opened) owns it while it has focus.
  if (combo === 'Escape' && isTyping(event.target)) return null;
  return combo;
}

/**
 * Subscribe to shortcuts. Handlers live in a ref so a component can pass a fresh closure
 * every render without re-binding the listener on every keystroke.
 */
export function useShortcuts(handlers: Record<string, () => void>, enabled = true): void {
  const bindings = useRef(handlers);
  bindings.current = handlers;

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent) {
      const combo = shortcutCombo(event);
      if (!combo) return;

      const find = (wanted: string) => table().find((entry) => normalizeKeyCombo(entry.keyCode) === wanted)?.action;
      // Shift with a function key always reached the plain F-key's action; keep it that way.
      const action = find(combo) ?? (/^Shift\+F\d+$/.test(combo) ? find(combo.slice('Shift+'.length)) : undefined);
      if (!action) return;

      const handler = bindings.current[action];
      if (!handler) return;

      event.preventDefault();
      handler();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

/**
 * Keys bound to function buttons (the built-in actions keep theirs in the table above). `bindings`
 * maps a normalised combo to what it runs.
 */
export function useComboShortcuts(bindings: Map<string, () => void>, enabled = true): void {
  const current = useRef(bindings);
  current.current = bindings;

  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(event: KeyboardEvent) {
      const combo = shortcutCombo(event);
      if (!combo) return;
      const run = current.current.get(combo);
      if (!run) return;
      event.preventDefault();
      run();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

/**
 * Browser keys the till must never hand to the browser, even while a dialog is open.
 * `useShortcuts` detaches while a modal is up (typing there belongs to the field), but
 * F5 (reload) and F11 (fullscreen) must still be swallowed: otherwise opening Hold and
 * pressing F5 reloads the page and wipes the in-memory bill. Capture-phase, so it runs
 * before any dialog handler. F12 cannot be intercepted and is not bound by default.
 *
 * `preventDefault` is what actually stops the browser — the reload is the default action
 * of the key. Never call `stopPropagation` here: it also swallows the event before the
 * till's own window listeners (bubble phase) can run, which quietly killed the F5
 * "remove line" and F11 "reprint" shortcuts entirely.
 */
export function useBrowserKeyGuard(active = true): void {
  useEffect(() => {
    if (!active) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'F5' || event.key === 'F11') {
        event.preventDefault();
      }
    }
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [active]);
}